/**
 * ML / CV Companion tools — COPILOT_MASTER_PLAN subsystem N.
 *
 * Registers the high-value ML/CV inference endpoints as first-class Companion
 * tools over the LIVE `/ai/ml/*` + CV `/api/v2/analyze` routes (wrapped by
 * `ml-tools.service.ts`). Each tool:
 *   • declares an input schema (subsystem C),
 *   • DERIVES defaults from the current page's entity context (subsystem B) so
 *     on a risk detail page it can propose "lancer une prédiction ML sur ce
 *     risque" pre-filling from the viewed record,
 *   • is `read` side-effect (advisory — no HITL to RUN; downstream createRecord
 *     stays HITL-gated per subsystem H),
 *   • returns a provenance-tagged <ToolCard kind='ml'|'cv'> for the window
 *     message stream + feeds `output` back into the agent-runner chain.
 */

import {
  runMlModel,
  runCvAnalyze,
  isMissingEndpoint,
  type MlToolResult,
} from '@/services/ml-tools.service'
import type { CompanionTool, ToolContext, ToolRunResult, ToolArgSpec } from './types'

// ── entity-context feature derivation ─────────────────────────────────────────

function entityData(ctx: ToolContext): Record<string, unknown> {
  return (ctx.entity?.data ?? {}) as Record<string, unknown>
}

function pick<T = unknown>(obj: Record<string, unknown>, ...keys: string[]): T | undefined {
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k] as T
  }
  return undefined
}

function numArg(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return fallback
}

function strArg(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback
}

/** Merge explicit args over entity-derived defaults over static fallbacks. */
function mergeFeatures(
  args: Record<string, unknown>,
  derived: Record<string, unknown>,
  fallback: Record<string, unknown> = {},
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...fallback, ...derived }
  for (const [k, v] of Object.entries(args)) {
    if (v !== undefined && v !== null && v !== '') out[k] = v
  }
  return out
}

function mlResultToRun(model: string, r: MlToolResult): ToolRunResult {
  if (!r.ok) {
    return {
      ok: false,
      detail: r.missing ? `${model} : modèle non déployé ici` : `${model} : ${r.error ?? 'échec'}`,
    }
  }
  return {
    ok: true,
    detail: `${model} : prédiction générée (${r.source})`,
    // Feed the whole result into {{stepN.field}} chaining AND expose the scalars.
    output: { ...r.result, result: r.result, source: r.source, credit: r.credit ?? null },
    card: {
      kind: 'ml',
      title: model,
      source: r.source,
      credit: r.credit ?? null,
      rows: r.result,
    },
  }
}

// ── ML tool factory ───────────────────────────────────────────────────────────

interface MlToolDef {
  name: string
  description: string
  endpoint: string
  resultKeys: string[]
  group?: 'ml'
  argsSchema: ToolArgSpec[]
  /** Static fallback payload merged UNDER derived + explicit args. */
  fallback: Record<string, unknown>
  /** Derive features from the current entity context. */
  derive: (ctx: ToolContext) => Record<string, unknown>
}

function makeMlTool(def: MlToolDef): CompanionTool {
  return {
    name: def.name,
    description: def.description,
    argsSchema: def.argsSchema,
    mode: 'read',
    hitl: false,
    surfaces: ['web'],
    group: 'ml',
    run: async (args, ctx) => {
      const features = mergeFeatures(args, def.derive(ctx), def.fallback)
      const r = await runMlModel(def.endpoint, features, def.resultKeys)
      return mlResultToRun(def.description, r)
    },
  }
}

// ── ML tool catalogue (8 live predictors + risk/anomaly/forecast) ─────────────

export const ML_TOOLS: CompanionTool[] = [
  makeMlTool({
    name: 'predictGap',
    description: 'Prédiction des écarts de conformité',
    endpoint: '/ai/ml/gap-prediction',
    resultKeys: ['prediction'],
    fallback: { historical_gaps: [] },
    argsSchema: [
      { name: 'framework', type: 'string', description: 'Cadre (ISO27001, ...)' },
      { name: 'current_score', type: 'number' },
      { name: 'evidence_completeness', type: 'number' },
      { name: 'control_count', type: 'number' },
    ],
    derive: (ctx) => {
      const d = entityData(ctx)
      return {
        framework: strArg(pick(d, 'framework', 'framework_code', 'standard'), 'ISO27001'),
        current_score: numArg(pick(d, 'score', 'current_score', 'maturity_score'), 72),
        evidence_completeness: numArg(pick(d, 'evidence_completeness', 'coverage'), 0.65),
        control_count: numArg(pick(d, 'control_count', 'controls'), 114),
      }
    },
  }),
  makeMlTool({
    name: 'scoreControlEffectiveness',
    description: 'Efficacité et défaillance d’un contrôle',
    endpoint: '/ai/ml/control-effectiveness',
    resultKeys: ['prediction'],
    fallback: { age_days: 365, last_test_result: 'pass' },
    argsSchema: [
      { name: 'control_type', type: 'string' },
      { name: 'test_frequency', type: 'number' },
      { name: 'finding_count', type: 'number' },
      { name: 'automation_level', type: 'number' },
    ],
    derive: (ctx) => {
      const d = entityData(ctx)
      return {
        control_type: strArg(pick(d, 'control_type', 'type', 'category'), 'access_control'),
        test_frequency: numArg(pick(d, 'test_frequency', 'frequency'), 4),
        finding_count: numArg(pick(d, 'finding_count', 'open_findings', 'findings'), 2),
        automation_level: numArg(pick(d, 'automation_level', 'automation'), 0.5),
      }
    },
  }),
  makeMlTool({
    name: 'predictVendorRisk',
    description: 'Risque prédictif d’un fournisseur',
    endpoint: '/ai/ml/vendor-risk',
    resultKeys: ['prediction'],
    fallback: { soc2_compliant: true, iso27001: false, gdpr: true, contract_value: 120000 },
    argsSchema: [
      { name: 'tier', type: 'number' },
      { name: 'sla_breaches_12m', type: 'number' },
      { name: 'financial_health_score', type: 'number' },
      { name: 'dependency_level', type: 'string', enum: ['low', 'medium', 'high', 'critical'] },
    ],
    derive: (ctx) => {
      const d = entityData(ctx)
      return {
        tier: numArg(pick(d, 'tier', 'vendor_tier'), 2),
        sla_breaches_12m: numArg(pick(d, 'sla_breaches_12m', 'sla_breaches'), 1),
        financial_health_score: numArg(pick(d, 'financial_health_score', 'financial_score'), 68),
        dependency_level: strArg(pick(d, 'dependency_level', 'dependency'), 'medium'),
      }
    },
  }),
  makeMlTool({
    name: 'predictComplianceDrift',
    description: 'Dérive de conformité',
    endpoint: '/ai/ml/compliance-drift',
    resultKeys: ['drift'],
    fallback: { series: [88, 86, 85, 83, 81, 80], baseline: 90, direction: 'down' },
    argsSchema: [
      { name: 'series', type: 'array' },
      { name: 'baseline', type: 'number' },
      { name: 'direction', type: 'string', enum: ['up', 'down'] },
    ],
    derive: (ctx) => {
      const d = entityData(ctx)
      const series = pick(d, 'series', 'history', 'scores')
      return { ...(Array.isArray(series) ? { series } : {}), baseline: numArg(pick(d, 'baseline', 'target'), 90) }
    },
  }),
  makeMlTool({
    name: 'kriEarlyWarning',
    description: 'Alerte précoce KRI',
    endpoint: '/ai/ml/kri-early-warning',
    resultKeys: ['early_warning'],
    fallback: { values: [42, 45, 49, 54, 58], threshold: 70, direction: 'upper', horizon: 6 },
    argsSchema: [
      { name: 'values', type: 'array' },
      { name: 'threshold', type: 'number' },
      { name: 'direction', type: 'string', enum: ['upper', 'lower'] },
      { name: 'horizon', type: 'number' },
    ],
    derive: (ctx) => {
      const d = entityData(ctx)
      const values = pick(d, 'values', 'kri_values', 'history')
      return {
        ...(Array.isArray(values) ? { values } : {}),
        threshold: numArg(pick(d, 'threshold'), 70),
      }
    },
  }),
  makeMlTool({
    name: 'incidentSeverity',
    description: 'Triage de sévérité d’un incident',
    endpoint: '/ai/ml/incident-severity',
    resultKeys: ['triage'],
    fallback: { category: 'security', data_exposed: true, regulatory: true },
    argsSchema: [
      { name: 'description', type: 'string' },
      { name: 'affected_users', type: 'number' },
      { name: 'systems_down', type: 'number' },
      { name: 'financial_impact', type: 'number' },
    ],
    derive: (ctx) => {
      const d = entityData(ctx)
      return {
        description: strArg(pick(d, 'description', 'title', 'summary'), 'Incident de sécurité'),
        affected_users: numArg(pick(d, 'affected_users', 'impacted_users'), 100),
        systems_down: numArg(pick(d, 'systems_down', 'systems_affected'), 1),
        financial_impact: numArg(pick(d, 'financial_impact', 'estimated_loss'), 50000),
      }
    },
  }),
  makeMlTool({
    name: 'predictSlaBreach',
    description: 'Risque de dépassement SLA',
    endpoint: '/ai/ml/sla-breach',
    resultKeys: ['prediction'],
    fallback: { reassignments: 2, dependency_count: 1, blockers: 1, owner_open_items: 7 },
    argsSchema: [
      { name: 'days_open', type: 'number' },
      { name: 'sla_days', type: 'number' },
      { name: 'priority', type: 'string', enum: ['low', 'medium', 'high', 'critical'] },
      { name: 'percent_complete', type: 'number' },
    ],
    derive: (ctx) => {
      const d = entityData(ctx)
      return {
        days_open: numArg(pick(d, 'days_open', 'age_days'), 12),
        sla_days: numArg(pick(d, 'sla_days', 'sla'), 15),
        priority: strArg(pick(d, 'priority', 'severity'), 'high'),
        percent_complete: numArg(pick(d, 'percent_complete', 'progress'), 40),
      }
    },
  }),
  makeMlTool({
    name: 'predictControlFailure',
    description: 'Probabilité de défaillance d’un contrôle',
    endpoint: '/ai/ml/control-failure',
    resultKeys: ['prediction'],
    fallback: { automation: 'semi-automated', test_interval_days: 90, test_history: ['pass', 'pass', 'fail', 'pass', 'fail'] },
    argsSchema: [
      { name: 'test_history', type: 'array' },
      { name: 'control_maturity', type: 'number' },
      { name: 'criticality', type: 'string', enum: ['low', 'medium', 'high', 'critical'] },
      { name: 'days_since_last_test', type: 'number' },
    ],
    derive: (ctx) => {
      const d = entityData(ctx)
      return {
        control_maturity: numArg(pick(d, 'control_maturity', 'maturity'), 3),
        criticality: strArg(pick(d, 'criticality', 'severity'), 'high'),
        days_since_last_test: numArg(pick(d, 'days_since_last_test'), 45),
      }
    },
  }),
  // ── Generic risk-scoring / anomaly / forecast (subsystem N headline tools) ──
  {
    name: 'predictRisk',
    description: 'Score de risque ML',
    argsSchema: [
      { name: 'risk_id', type: 'string', description: 'Id du risque (déduit de la page)' },
    ],
    mode: 'read',
    hitl: false,
    surfaces: ['web'],
    group: 'ml',
    run: async (args, ctx) => {
      const d = entityData(ctx)
      const riskId = strArg(args.risk_id ?? pick(d, 'id', 'risk_id', 'entity_id'), '')
      // Single-row BATCH POST (the only live risk-score contract). The governance
      // `POST /ai/ml/risk-score/batch` accepts a loose feature list — no per-id
      // GET exists (that path 404s on ai-orch). We derive probability/impact/
      // category/residual from the viewed entity and score exactly one row.
      const feature: Record<string, unknown> = {
        ...(riskId ? { risk_id: riskId, ref_code: strArg(pick(d, 'ref_code', 'code', 'reference'), riskId) } : {}),
        probability: numArg(pick(d, 'probability', 'likelihood', 'inherent_probability'), 3),
        impact: numArg(pick(d, 'impact', 'inherent_impact', 'severity'), 3),
        residual_score: numArg(pick(d, 'residual_score', 'residual', 'score'), 0) || undefined,
        category: strArg(pick(d, 'category', 'category_id', 'type'), 'operational'),
      }
      const raw = (await (async () => {
        const { post } = await import('@/lib/api/client')
        try {
          return await post<Record<string, unknown>>('/ai/ml/risk-score/batch', { risks: [feature] }, { timeout: 120_000 })
        } catch (err) {
          if (isMissingEndpoint(err)) return { __missing: true } as Record<string, unknown>
          throw err
        }
      })()) as Record<string, unknown>
      if (raw?.__missing) {
        return { ok: false, detail: 'Score de risque ML : modèle non déployé ici' }
      }
      // Response: { scores:[{...ml_score,level,prediction,confidence,source}], source, tenant_baseline }.
      const scores = Array.isArray(raw?.scores)
        ? (raw.scores as Record<string, unknown>[])
        : Array.isArray(raw?.items)
          ? (raw.items as Record<string, unknown>[])
          : []
      const inner = (scores[0] ?? {}) as Record<string, unknown>
      const source =
        typeof inner.source === 'string' ? inner.source : typeof raw?.source === 'string' ? raw.source : 'ml-inference'
      const rows: Record<string, unknown> = {
        ...inner,
        ...(raw?.tenant_baseline != null ? { tenant_baseline: raw.tenant_baseline } : {}),
      }
      return {
        ok: true,
        detail: `Score de risque ML calculé (${source})`,
        output: { ...rows, source },
        card: { kind: 'ml', title: 'Score de risque ML', source, rows },
      }
    },
  },
  {
    name: 'detectAnomalies',
    description: 'Détection d’anomalies',
    argsSchema: [
      { name: 'metric_name', type: 'string' },
      { name: 'current_value', type: 'number' },
      { name: 'module', type: 'string' },
    ],
    mode: 'read',
    hitl: false,
    surfaces: ['web'],
    group: 'ml',
    run: async (args, ctx) => {
      const d = entityData(ctx)
      const payload = {
        metric_name: strArg(args.metric_name ?? pick(d, 'metric_name', 'metric', 'title'), 'risk_score'),
        current_value: numArg(args.current_value ?? pick(d, 'current_value', 'value', 'score'), 0),
        module: strArg(args.module ?? ctx.module, 'risk'),
      }
      const r = await runMlModel('/ai/ml/anomaly/detect', payload, ['anomaly', 'result', 'detection'])
      return mlResultToRun('Détection d’anomalies', r)
    },
  },
  {
    name: 'forecast',
    description: 'Prévision d’une métrique',
    argsSchema: [
      { name: 'metric_name', type: 'string' },
      { name: 'module', type: 'string' },
      { name: 'horizon_months', type: 'number' },
    ],
    mode: 'read',
    hitl: false,
    surfaces: ['web'],
    group: 'ml',
    run: async (args, ctx) => {
      const d = entityData(ctx)
      const payload = {
        metric_name: strArg(args.metric_name ?? pick(d, 'metric_name', 'metric', 'title'), 'compliance_score'),
        module: strArg(args.module ?? ctx.module, 'compliance'),
        horizon_months: numArg(args.horizon_months, 6),
        historical_data: Array.isArray(pick(d, 'historical_data', 'history')) ? pick(d, 'historical_data', 'history') : [],
      }
      const r = await runMlModel('/ai/ml/forecast', payload, ['forecast', 'prediction', 'result'])
      return mlResultToRun('Prévision', r)
    },
  },
]

// ── CV tools (perception chain — file → extraction) ───────────────────────────

/**
 * Build the CV tools. `getFile()` resolves the current dropped file at run-time
 * (the window owns file state; the tool reads it lazily from ctx.file OR the
 * getter so a plan can reference a file dropped after the plan was built).
 */
export const CV_TOOLS: CompanionTool[] = [
  {
    name: 'analyzeDocument',
    description: 'Analyse OCR/extraction d’un document (CV)',
    argsSchema: [
      { name: 'model', type: 'string', description: 'Modèle CV (ocr, layout, classification, extraction)' },
      { name: 'file', type: 'file', required: true },
    ],
    mode: 'read',
    hitl: false,
    surfaces: ['web'],
    group: 'cv',
    run: async (args, ctx) => {
      const file = (args.file as File | undefined) ?? ctx.file ?? null
      if (!file) return { ok: false, detail: 'Aucun fichier fourni pour l’analyse CV.' }
      const model = strArg(args.model, 'extraction')
      const r = await runCvAnalyze(file, model)
      if (!r.ok) {
        return { ok: false, detail: r.missing ? 'CV non déployé ici' : `Analyse CV : ${r.error ?? 'échec'}` }
      }
      return {
        ok: true,
        detail: `Document analysé (${model})`,
        // Extracted text + fields feed a downstream fillForm/import step.
        output: {
          ...r.result,
          document_text: r.documentText ?? '',
          fields: r.fields ?? {},
          source: r.source,
        },
        card: {
          kind: 'cv',
          title: `Analyse document — ${model}`,
          source: r.source,
          credit: r.credit ?? null,
          rows: r.result,
          summary: r.documentText ? `${r.documentText.slice(0, 400)}…` : undefined,
        },
      }
    },
  },
  {
    name: 'classifyDocument',
    description: 'Classification d’un document (CV)',
    argsSchema: [{ name: 'file', type: 'file', required: true }],
    mode: 'read',
    hitl: false,
    surfaces: ['web'],
    group: 'cv',
    run: async (args, ctx) => {
      const file = (args.file as File | undefined) ?? ctx.file ?? null
      if (!file) return { ok: false, detail: 'Aucun fichier fourni pour la classification.' }
      const r = await runCvAnalyze(file, 'classification')
      if (!r.ok) return { ok: false, detail: r.missing ? 'CV non déployé ici' : `Classification : ${r.error ?? 'échec'}` }
      return {
        ok: true,
        detail: 'Document classifié',
        output: { ...r.result, source: r.source },
        card: { kind: 'cv', title: 'Classification document', source: r.source, credit: r.credit ?? null, rows: r.result },
      }
    },
  },
]

export const ALL_ML_CV_TOOLS: CompanionTool[] = [...ML_TOOLS, ...CV_TOOLS]
